/**
 * Auth Context
 * Manages user authentication state and auth operations
 * 
 * Uses a hybrid approach for token storage:
 * - Primary: httpOnly cookies (secure, handled by backend)
 * - Fallback: localStorage (for mobile browsers that block third-party cookies)
 */
import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios, { initializeCsrf } from '../lib/axios'; // Use configured axios with CSRF

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// localStorage key for token backup (mobile fallback)
const TOKEN_STORAGE_KEY = 'auth_token_backup';
const USER_STORAGE_KEY = 'auth_user_backup';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

// Helper to safely access localStorage
const safeStorage = {
  get: (key) => {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set: (key, value) => {
    try {
      localStorage.setItem(key, value);
    } catch {
      // Ignore storage errors (private browsing, etc.)
    }
  },
  remove: (key) => {
    try {
      localStorage.removeItem(key);
    } catch {
      // Ignore
    }
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null); // Token state kept for WebSocket auth only
  const [loading, setLoading] = useState(true);
  
  // Guard to prevent fetchUser from overwriting OAuth login state
  const skipNextFetchRef = useRef(false);

  // Track page visit for daily visitor count
  useEffect(() => {
    const trackVisit = async () => {
      try {
        // Only track once per session
        const visited = sessionStorage.getItem('visit_tracked');
        if (!visited) {
          await axios.post(`${API}/track-visit`, { page: 'app_load' });
          sessionStorage.setItem('visit_tracked', 'true');
        }
      } catch (error) {
        // Silent fail - don't disrupt user experience
      }
    };
    trackVisit();
  }, []);

  // Check auth status on mount (cookie is sent automatically)
  useEffect(() => {
    fetchUser();
  }, []);

  // Listen for session-expired events from axios interceptor
  // This handles 401 errors gracefully by clearing the user state
  useEffect(() => {
    let sessionExpiredHandled = false;
    
    const handleSessionExpired = (event) => {
      // Only handle once per page load to prevent multiple logouts
      if (sessionExpiredHandled) return;
      
      // Only clear state if we think we're logged in
      if (user) {
        sessionExpiredHandled = true;
        console.log('Session expired, clearing local auth state');
        setUser(null);
        setToken(null);
        // Clear localStorage backup
        safeStorage.remove(TOKEN_STORAGE_KEY);
        safeStorage.remove(USER_STORAGE_KEY);
      }
    };

    window.addEventListener('session-expired', handleSessionExpired);
    return () => window.removeEventListener('session-expired', handleSessionExpired);
  }, [user]);

  const fetchUser = async () => {
    // Skip if we just did an OAuth login (prevents race condition)
    if (skipNextFetchRef.current) {
      skipNextFetchRef.current = false;
      setLoading(false);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      // Store token for WebSocket auth (from response if available, otherwise keep existing)
      if (response.data?.token) {
        setToken(response.data.token);
        // Update localStorage backup
        safeStorage.set(TOKEN_STORAGE_KEY, response.data.token);
        safeStorage.set(USER_STORAGE_KEY, JSON.stringify(response.data));
      }
    } catch (error) {
      // Cookie auth failed - try localStorage backup (for mobile browsers)
      const backupToken = safeStorage.get(TOKEN_STORAGE_KEY);
      const backupUser = safeStorage.get(USER_STORAGE_KEY);
      
      if (backupToken && backupUser) {
        try {
          // Verify the backup token is still valid by making an authenticated request
          const verifyResponse = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${backupToken}` }
          });
          setUser(verifyResponse.data);
          setToken(backupToken);
          // Update stored user data
          safeStorage.set(USER_STORAGE_KEY, JSON.stringify(verifyResponse.data));
        } catch (verifyError) {
          // Backup token also invalid - clear everything
          setUser(null);
          setToken(null);
          safeStorage.remove(TOKEN_STORAGE_KEY);
          safeStorage.remove(USER_STORAGE_KEY);
        }
      } else {
        // No backup - not authenticated
        setUser(null);
        setToken(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    // Cookie is set by server, we just store token for WebSocket
    setToken(newToken);
    setUser(userData);
    // Store in localStorage as backup for mobile browsers
    if (newToken) {
      safeStorage.set(TOKEN_STORAGE_KEY, newToken);
    }
    if (userData) {
      safeStorage.set(USER_STORAGE_KEY, JSON.stringify(userData));
    }
    
    // Fetch CSRF token immediately after login (important for iOS Safari)
    try {
      await initializeCsrf(true); // Force refresh to get new token for this session
    } catch (e) {
      console.warn('Failed to initialize CSRF after login:', e);
    }
    
    return userData;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API}/auth/register`, userData);
    
    // Check if email verification is required
    if (response.data.email_verification_required) {
      // Return special response indicating verification needed
      // Include upload_token and user_id for business document upload
      return {
        email_verification_required: true,
        email: response.data.email,
        message: response.data.message,
        user_id: response.data.user_id,
        upload_token: response.data.upload_token
      };
    }
    
    // Normal registration - cookie is set by server, we just store token for WebSocket
    const { token: newToken, user: newUser } = response.data;
    setToken(newToken);
    setUser(newUser);
    // Store in localStorage as backup for mobile browsers
    if (newToken) {
      safeStorage.set(TOKEN_STORAGE_KEY, newToken);
    }
    if (newUser) {
      safeStorage.set(USER_STORAGE_KEY, JSON.stringify(newUser));
    }
    return newUser;
  };

  const logout = async () => {
    // Call server-side logout to invalidate token and clear cookie
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (error) {
      // Ignore errors - still clear local state
      console.log('Logout API call failed, clearing local state anyway');
    }
    setToken(null);
    setUser(null);
    // Clear localStorage backup
    safeStorage.remove(TOKEN_STORAGE_KEY);
    safeStorage.remove(USER_STORAGE_KEY);
  };
  
  const logoutAllDevices = async () => {
    // Logout from all devices
    try {
      await axios.post(`${API}/auth/logout-all-devices`);
    } catch (error) {
      console.error('Failed to logout from all devices:', error);
    }
    setToken(null);
    setUser(null);
    // Clear localStorage backup
    safeStorage.remove(TOKEN_STORAGE_KEY);
    safeStorage.remove(USER_STORAGE_KEY);
  };

  const loginWithToken = (newToken, userData) => {
    // For OAuth callbacks - token is already set as cookie by server
    // Set skip flag to prevent fetchUser from running and overwriting this state
    skipNextFetchRef.current = true;
    setToken(newToken);
    setUser(userData);
    
    // Store in localStorage as backup for mobile browsers that block third-party cookies
    if (newToken) {
      safeStorage.set(TOKEN_STORAGE_KEY, newToken);
    }
    if (userData) {
      safeStorage.set(USER_STORAGE_KEY, JSON.stringify(userData));
    }
    
    // Fetch CSRF token immediately after OAuth login (important for iOS Safari)
    initializeCsrf(true).catch(e => {
      console.warn('Failed to initialize CSRF after OAuth login:', e);
    });
  };

  const updateProfile = async (data) => {
    const response = await axios.put(`${API}/auth/profile`, data);
    setUser(response.data);
    return response.data;
  };

  // Refresh user data from server (used by WelcomeFlow after completing onboarding)
  const refreshUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      if (response.data?.token) {
        setToken(response.data.token);
      }
      return response.data;
    } catch (error) {
      console.error('Error refreshing user:', error);
      return null;
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      loading, 
      login, 
      register, 
      logout, 
      logoutAllDevices, 
      loginWithToken, 
      updateProfile, 
      fetchUser,
      refreshUser 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
